import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

function formatFCFA(amount) {
  const n = Number(amount || 0);
  return `${n.toLocaleString("fr-FR")} FCFA`;
}

function formatPaymentMethod(method) {
  switch (method) {
    case "mobile_money":
      return "Mobile Money";
    case "card":
      return "Carte bancaire";
    default:
      return method || "Non défini";
  }
}

function formatStatus(status) {
  switch (status) {
    case "pending_admin_validation":
      return "En attente de validation admin";
    case "validated":
      return "Validée";
    case "rejected":
      return "Refusée";
    default:
      return status || "En attente";
  }
}

export default function VendorSignupSuccess() {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state || {};
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimateIn(true), 80);
    return () => clearTimeout(t);
  }, []);

  const hasData = useMemo(() => {
    return Boolean(
      state?.shopName ||
        state?.fullName ||
        state?.phone ||
        state?.packName ||
        state?.status
    );
  }, [state]);

  const summary = {
    shopName: state?.shopName || "—",
    fullName: state?.fullName || "—",
    phone: state?.phone || "—",
    email: state?.email || "—",
    city: state?.city || "—",
    packName: state?.packName || "—",
    totalFee:
      typeof state?.totalFee === "number" ? formatFCFA(state.totalFee) : "—",
    quantity: state?.quantity || 1,
    totalMonths: state?.totalMonths || 0,
    paymentMethod: formatPaymentMethod(state?.paymentMethod),
    status: formatStatus(state?.status || "pending_admin_validation"),
  };

  return (
    <div className="vss-page">
      <div className="vss-shell">
        <div className={`vss-card ${animateIn ? "is-visible" : ""}`}>
          <div className="vss-hero">
            <div className="vss-icon-wrap">
              <div className="vss-icon-ring" />
              <div className="vss-icon-core">
                <svg
                  viewBox="0 0 24 24"
                  className="vss-check"
                  aria-hidden="true"
                >
                  <path
                    d="M20 6L9 17l-5-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            <div className="vss-kicker">Demande envoyée</div>
            <h1 className="vss-title">
              Votre inscription vendeur a bien été enregistrée
            </h1>
            <p className="vss-subtitle">
              Merci. Votre demande a été transmise à l’équipe GoShop pour
              vérification et validation.
            </p>

            <div className="vss-status-pill">
              {summary.status}
            </div>
          </div>

          {!hasData ? (
            <div className="vss-fallback">
              <p>
                Nous n’avons pas trouvé les détails de la demande dans cette
                session.
              </p>

              <div className="vss-actions center">
                <button
                  type="button"
                  className="vss-btn vss-btn-dark"
                  onClick={() => navigate("/inscription-vendeur")}
                >
                  Revenir au formulaire
                </button>

                <Link to="/" className="vss-btn vss-btn-light">
                  Aller à l’accueil
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="vss-grid">
                <section className="vss-panel">
                  <div className="vss-panel-head">
                    <h2>Récapitulatif</h2>
                    <p>Informations soumises lors de votre demande.</p>
                  </div>

                  <div className="vss-summary-list">
                    <div className="vss-row">
                      <span>Boutique</span>
                      <strong>{summary.shopName}</strong>
                    </div>

                    <div className="vss-row">
                      <span>Responsable</span>
                      <strong>{summary.fullName}</strong>
                    </div>

                    <div className="vss-row">
                      <span>Téléphone</span>
                      <strong>{summary.phone}</strong>
                    </div>

                    <div className="vss-row">
                      <span>Email</span>
                      <strong>{summary.email}</strong>
                    </div>

                    <div className="vss-row">
                      <span>Ville / Pays</span>
                      <strong>{summary.city}</strong>
                    </div>

                    <div className="vss-row">
                      <span>Pack choisi</span>
                      <strong>{summary.packName}</strong>
                    </div>

                    <div className="vss-row">
                      <span>Durée / quantité</span>
                      <strong>
                        {summary.quantity} période(s)
                        {summary.totalMonths
                          ? ` · ${summary.totalMonths} mois`
                          : ""}
                      </strong>
                    </div>

                    <div className="vss-row">
                      <span>Mode de paiement</span>
                      <strong>{summary.paymentMethod}</strong>
                    </div>

                    <div className="vss-row total">
                      <span>Montant total</span>
                      <strong>{summary.totalFee}</strong>
                    </div>
                  </div>
                </section>

                <aside className="vss-panel vss-side">
                  <div className="vss-panel-head">
                    <h2>Prochaines étapes</h2>
                    <p>Voici ce qui va se passer ensuite.</p>
                  </div>

                  <ol className="vss-steps">
                    <li>
                      <span className="vss-step-index">1</span>
                      <div>
                        <strong>Vérification par l’admin</strong>
                        <p>
                          L’équipe GoShop vérifie les informations de votre
                          boutique et votre pack.
                        </p>
                      </div>
                    </li>

                    <li>
                      <span className="vss-step-index">2</span>
                      <div>
                        <strong>Validation ou retour</strong>
                        <p>
                          Votre demande peut être validée ou faire l’objet
                          d’une demande de précision.
                        </p>
                      </div>
                    </li>

                    <li>
                      <span className="vss-step-index">3</span>
                      <div>
                        <strong>Activation du compte vendeur</strong>
                        <p>
                          Une fois validé, vous recevrez les instructions pour
                          accéder à votre espace vendeur.
                        </p>
                      </div>
                    </li>
                  </ol>

                  <div className="vss-note">
                    Conseil : gardez votre téléphone et votre email actifs pour
                    recevoir rapidement la suite du processus.
                  </div>
                </aside>
              </div>

              <div className="vss-actions">
                <Link to="/" className="vss-btn vss-btn-dark">
                  Retour à l’accueil
                </Link>

                <Link to="/become-vendor" className="vss-btn vss-btn-light">
                  Voir la page vendeurs
                </Link>

                <button
                  type="button"
                  className="vss-btn vss-btn-light"
                  onClick={() => window.print()}
                >
                  Imprimer le récapitulatif
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .vss-page{
          min-height:100vh;
          background:
            radial-gradient(circle at top left, #fff7ed 0, #f8fafc 45%, #eef2ff 100%);
          padding: 28px 14px 40px;
          font-family: 'Bogle', Helvetica Neue, Helvetica, Arial, sans-serif;
          color:#111827;
        }

        .vss-shell{
          max-width: 1120px;
          margin: 0 auto;
        }

        .vss-card{
          background:#fff;
          border:1px solid #e5e7eb;
          border-radius:28px;
          box-shadow:0 22px 60px rgba(15,23,42,.10);
          padding:24px;
          opacity:0;
          transform:translateY(16px) scale(.985);
          transition:all .45s ease;
        }

        .vss-card.is-visible{
          opacity:1;
          transform:translateY(0) scale(1);
        }

        .vss-hero{
          text-align:center;
          padding:8px 0 18px;
          border-bottom:1px solid #eef2f7;
          margin-bottom:20px;
        }

        .vss-icon-wrap{
          position:relative;
          width:96px;
          height:96px;
          margin:0 auto 14px;
        }

        .vss-icon-ring{
          position:absolute;
          inset:0;
          border-radius:999px;
          background:
            radial-gradient(circle, rgba(34,197,94,.18) 0%, rgba(34,197,94,.06) 52%, transparent 70%);
          animation:vssPulse 1.8s ease-in-out infinite;
        }

        .vss-icon-core{
          position:absolute;
          inset:14px;
          border-radius:999px;
          background:linear-gradient(135deg,#16a34a 0%, #22c55e 100%);
          display:flex;
          align-items:center;
          justify-content:center;
          color:#fff;
          box-shadow:0 14px 28px rgba(34,197,94,.28);
        }

        .vss-check{
          width:34px;
          height:34px;
        }

        .vss-kicker{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          padding:6px 12px;
          border-radius:999px;
          background:#ecfdf5;
          color:#166534;
          font-size:12px;
          font-weight:900;
          letter-spacing:.04em;
          text-transform:uppercase;
          margin-bottom:10px;
        }

        .vss-title{
          font-size:30px;
          line-height:1.15;
          font-weight:900;
          color:#0f172a;
          margin:0 0 8px;
        }

        .vss-subtitle{
          max-width:720px;
          margin:0 auto;
          color:#64748b;
          font-size:14px;
          line-height:1.65;
        }

        .vss-status-pill{
          margin:14px auto 0;
          width:fit-content;
          padding:8px 14px;
          border-radius:999px;
          background:#fff7ed;
          color:#c2410c;
          border:1px solid #fed7aa;
          font-size:12px;
          font-weight:800;
        }

        .vss-grid{
          display:grid;
          grid-template-columns:minmax(0,1.35fr) minmax(300px,.8fr);
          gap:18px;
        }

        .vss-panel{
          background:#fff;
          border:1px solid #e5e7eb;
          border-radius:22px;
          padding:18px;
          box-shadow:0 8px 24px rgba(15,23,42,.04);
        }

        .vss-panel-head h2{
          margin:0;
          font-size:18px;
          font-weight:900;
          color:#111827;
        }

        .vss-panel-head p{
          margin:4px 0 0;
          font-size:12px;
          color:#6b7280;
        }

        .vss-summary-list{
          margin-top:14px;
          display:flex;
          flex-direction:column;
          gap:0;
        }

        .vss-row{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:16px;
          padding:11px 0;
          border-bottom:1px solid #f1f5f9;
          font-size:13px;
        }

        .vss-row span{
          color:#64748b;
        }

        .vss-row strong{
          color:#111827;
          text-align:right;
          max-width:55%;
          word-break:break-word;
        }

        .vss-row.total{
          padding-top:14px;
          margin-top:2px;
        }

        .vss-row.total strong{
          color:#16a34a;
          font-size:15px;
        }

        .vss-steps{
          list-style:none;
          padding:0;
          margin:14px 0 0;
          display:flex;
          flex-direction:column;
          gap:14px;
        }

        .vss-steps li{
          display:flex;
          gap:12px;
          align-items:flex-start;
        }

        .vss-step-index{
          min-width:28px;
          width:28px;
          height:28px;
          border-radius:999px;
          background:#eef2ff;
          color:#4338ca;
          font-weight:900;
          font-size:12px;
          display:flex;
          align-items:center;
          justify-content:center;
          margin-top:2px;
        }

        .vss-steps strong{
          display:block;
          color:#111827;
          font-size:13px;
          margin-bottom:2px;
        }

        .vss-steps p{
          margin:0;
          font-size:12px;
          color:#6b7280;
          line-height:1.6;
        }

        .vss-note{
          margin-top:16px;
          padding:12px 14px;
          border-radius:16px;
          background:#f8fafc;
          border:1px solid #e5e7eb;
          color:#475569;
          font-size:12px;
          line-height:1.6;
        }

        .vss-actions{
          margin-top:18px;
          display:flex;
          flex-wrap:wrap;
          gap:10px;
          justify-content:center;
        }

        .vss-actions.center{
          justify-content:center;
        }

        .vss-btn{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          min-height:44px;
          padding:10px 18px;
          border-radius:999px;
          font-size:14px;
          font-weight:800;
          text-decoration:none;
          border:1px solid transparent;
          cursor:pointer;
          transition:all .2s ease;
        }

        .vss-btn-dark{
          background:#111827;
          color:#fff;
          border-color:#111827;
        }

        .vss-btn-dark:hover{
          background:#f97316;
          border-color:#f97316;
          color:#fff;
        }

        .vss-btn-light{
          background:#fff;
          color:#111827;
          border-color:#d1d5db;
        }

        .vss-btn-light:hover{
          background:#f8fafc;
        }

        .vss-fallback{
          padding:18px 8px 4px;
          text-align:center;
        }

        .vss-fallback p{
          color:#6b7280;
          margin:0 0 14px;
        }

        @keyframes vssPulse{
          0%{transform:scale(.9); opacity:.8;}
          70%{transform:scale(1.12); opacity:.2;}
          100%{transform:scale(1.2); opacity:0;}
        }

        @media (max-width: 900px){
          .vss-grid{
            grid-template-columns:minmax(0,1fr);
          }
        }

        @media (max-width: 640px){
          .vss-page{
            padding:18px 10px 32px;
          }

          .vss-card{
            padding:16px;
            border-radius:22px;
          }

          .vss-title{
            font-size:24px;
          }

          .vss-row{
            flex-direction:column;
            gap:4px;
          }

          .vss-row strong{
            max-width:100%;
            text-align:left;
          }

          .vss-actions{
            flex-direction:column;
          }

          .vss-btn{
            width:100%;
          }
        }

        @media print{
          .vss-page{
            background:#fff;
            padding:0;
          }

          .vss-card{
            box-shadow:none;
            border:none;
            padding:0;
          }

          .vss-actions{
            display:none;
          }
        }
      `}</style>
    </div>
  );
}